import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  Users,
  UserPlus,
  ChevronRight,
  X,
} from "lucide-react";
import * as approvalTypeApi from "../../api/approval/type";
import { getMeta } from "../../api/approval/meta";
import FormContentManager from "../../components/common/form/FormContentManager";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import ApprovalLineModal from "../../components/approval/ApprovalLineModal";
import ReferenceModal from "../../components/approval/ReferenceModal";
import {
  listCustomApprovalLines,
  saveCustomApprovalLine,
  deleteCustomApprovalLine,
  saveApprovalLinesForDocument,
} from "../../api/approval";
import axios from "../../api/axios";

export default function Request() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeProjectId, dns } = useParams();
  const selectedType = location.state?.selectedType || null;
  const [isEditMode, setIsEditMode] = useState(Boolean(dns));
  const editorRef = useRef(null);

  const projectId = routeProjectId || localStorage.getItem("projectId") || "";
  const projectCode =
    localStorage.getItem("projectCode") ||
    localStorage.getItem("project_code") ||
    "";
  const pid = projectId || projectCode;

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: selectedType?.title || "",
    content: selectedType?.content || "",
    document_type: selectedType?.id || "",
    doc_type: selectedType?.code || selectedType?.doc_type || "",
    is_urgent: false,
  });

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    approvalSettings: true,
    docInfo: true,
    content: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 결재선 관련 상태
  const [approvalLines, setApprovalLines] = useState([]);
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [lines, setLines] = useState([]);
  const [isApprovalLineModalOpen, setIsApprovalLineModalOpen] = useState(false);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  // 사용자 목록 로드
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`/project/${pid}/users-without-me/`);
        if (response.data?.results?.length > 0) {
          setUsers(response.data.results);
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          setUsers(response.data);
        }
      } catch (error) {
        console.error("사용자 목록 API 오류:", error);
      }
    };
    if (pid) fetchUsers();
  }, [pid]);

  // 결재선 템플릿 로드
  const refreshLines = async () => {
    try {
      const responseLines = await listCustomApprovalLines();
      const list = Array.isArray(responseLines?.data?.results)
        ? responseLines.data.results
        : [];
      setLines(list);
    } catch (error) {
      console.error("결재선 목록 API 오류:", error);
      setLines([]);
    }
  };

  useEffect(() => {
    refreshLines();
  }, []);

  useEffect(() => {
    const loadList = async () => {
      if (!pid) return;
      setLoading(true);
      try {
        const res = await approvalTypeApi.getTypeList(pid);
        setTypes(res || []);

        if (!selectedType) {
          const general =
            res.find((t) => t.name === "일반" || t.title === "일반") || res[0];
          setForm((prev) => ({
            ...prev,
            document_type: general?.id ? String(general.id) : "",
            doc_type: general?.code || general?.doc_type || "",
            title: general?.title || "",
            content: general?.content || "",
          }));
        }
      } catch (err) {
        console.error("결재 문서유형 목록 불러오기 실패:", err);
        setTypes([]);
      } finally {
        setLoading(false);
      }
    };

    loadList();
  }, [pid, selectedType]);

  useEffect(() => {
    if (!dns) return;
    setIsEditMode(true);

    async function loadDetail() {
      try {
        const data = await getMeta(dns);
        setForm({
          title: data?.title || "",
          content: data?.meta.content || "",
          document_type: data?.meta.document_type?.id || "",
          doc_type: data?.meta.doc_type || "",
          is_urgent: data?.meta.is_urgent || false,
        });

        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setContent(
              data?.meta?.content || data?.content || ""
            );
          }
        }, 200);
      } catch (err) {
        console.error("결재 문서 상세 불러오기 실패:", err);
      }
    }

    loadDetail();
  }, [dns]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "document_type") {
      const valStr = String(value);
      const selected = types.find((t) => String(t.id) === valStr);
      setForm((prev) => ({
        ...prev,
        document_type: valStr,
        doc_type: selected?.code || prev.doc_type,
        title: selected?.title || prev.title,
        content: selected?.content || prev.content,
      }));
      if (editorRef.current && selected?.content) {
        editorRef.current.setContent(selected.content);
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // 결재선 관리 함수들
  const addApprover = (newApprover = null) => {
    if (newApprover) {
      setApprovalLines((prev) => [...prev, newApprover]);
      return;
    }
    const nextOrder =
      approvalLines.length > 0
        ? Math.max(...approvalLines.map((l) => l.order)) + 1
        : 1;
    setApprovalLines((prev) => [
      ...prev,
      {
        id: Date.now(),
        username: "",
        order: nextOrder,
        approval_type: "sequential",
        approval_category: "approval",
      },
    ]);
  };

  const removeApprover = (id) => {
    const updated = approvalLines
      .filter((l) => l.id !== id)
      .map((l, i) => ({ ...l, order: i + 1 }));
    setApprovalLines(updated);
  };

  const regroupAndNormalizeOrders = (linesArr) => {
    const groups = {};
    linesArr.forEach((l) => {
      if (!groups[l.order]) groups[l.order] = [];
      groups[l.order].push(l);
    });
    let cur = 1;
    const result = [];
    Object.keys(groups)
      .sort((a, b) => +a - +b)
      .forEach((order) => {
        const group = groups[order];
        group.forEach((l) => {
          l.order = cur;
          if (group.length > 1) l.approval_type = "parallel";
          result.push(l);
        });
        cur++;
      });
    return result;
  };

  const moveApproverByIndex = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    const fromLine = approvalLines[fromIndex];
    const toLine = approvalLines[toIndex];
    const updated = [...approvalLines];
    updated[fromIndex] = { ...fromLine, order: toLine.order };
    setApprovalLines(regroupAndNormalizeOrders(updated));
  };

  const moveApproverByIndexForReorder = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    const fromOrder = approvalLines[fromIndex]?.order;
    const toOrder = approvalLines[toIndex]?.order;
    if (!fromOrder || !toOrder) return;
    const swapped = approvalLines.map((l) => {
      if (l.order === fromOrder) return { ...l, order: toOrder };
      if (l.order === toOrder) return { ...l, order: fromOrder };
      return l;
    });
    setApprovalLines(regroupAndNormalizeOrders(swapped));
  };

  const handleApproverChange = (id, approver_id) => {
    setApprovalLines(
      approvalLines.map((l) => (l.id === id ? { ...l, approver_id } : l))
    );
  };

  const handleApprovalTypeChange = (id, approval_type) => {
    if (approval_type === "parallel") {
      const target = approvalLines.find((l) => l.id === id);
      if (!target) return;
      const updated = approvalLines.map((l) => {
        if (l.id === id) return { ...l, approval_type: "parallel" };
        if (l.order === target.order)
          return { ...l, approval_type: "parallel" };
        return l;
      });
      setApprovalLines(updated);
    } else {
      setApprovalLines(
        approvalLines.map((l) => (l.id === id ? { ...l, approval_type } : l))
      );
    }
  };

  const handleApprovalCategoryChange = (id, approval_category) => {
    setApprovalLines(
      approvalLines.map((l) => (l.id === id ? { ...l, approval_category } : l))
    );
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const approvers = approvalLines.map((l) => ({
        approver_id: l.approver_id,
        order: l.order,
        approval_category: l.approval_category || "approval",
      }));
      await saveCustomApprovalLine({ name: templateData.name, approvers });
      alert("결재선이 저장되었습니다.");
      await refreshLines();
    } catch (error) {
      console.error("템플릿 저장 실패:", error);
      alert("저장에 실패했습니다.");
    }
  };

  const onDeleteline = async (lineId) => {
    try {
      await deleteCustomApprovalLine(lineId);
      alert("결재선이 삭제되었습니다.");
      await refreshLines();
    } catch (error) {
      console.error("삭제 실패:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  // 결재선 저장 함수
  const saveApprovalLineForDoc = async (dnsOrId) => {
    if (approvalLines.length === 0) return;

    const hasEmptyApprover = approvalLines.some((line) => !line.approver_id);
    if (hasEmptyApprover) {
      alert("모든 결재자를 선택해주세요.");
      throw new Error("결재자 미선택");
    }

    const approvalLinesData = approvalLines.map((line, idx) => ({
      approver_id: line.approver_id,
      order: line.order ?? idx + 1,
      approval_type: line.approval_type ?? "sequential",
      approval_category: line.approval_category || "approval",
    }));

    const referenceData = referenceUserIds.map((userId) => ({
      user_id: userId,
    }));

    const payload = {
      doc_dns: dnsOrId,
      approval_lines: approvalLinesData,
      references: referenceData,
    };

    await saveApprovalLinesForDocument(payload);
  };

  const getBody = () => {
    const html = editorRef.current?.getContent() || "";

    return {
      layout: null,
      values: {
        project: projectId,
        doc_type: form.doc_type,
        document_type: form.document_type,
        is_urgent: form.is_urgent,
        title: form.title,
        content: html,
        meta: {
          title: form.title,
          content: html,
          doc_type: form.doc_type,
          is_urgent: form.is_urgent,
          document_type: form.document_type,
        },
      },
    };
  };

  const handleBack = () => navigate(-1);
  const triggerSave = () =>
    document.dispatchEvent(new CustomEvent("pmis-approval-save"));

  // 결재선 그룹화
  const orderGroups = {};
  approvalLines.forEach((line) => {
    if (!orderGroups[line.order]) orderGroups[line.order] = [];
    orderGroups[line.order].push(line);
  });

  return (
    <>
      <FormContentManager
        params={{ module: "approval", project: pid }}
        docName="결재 상신"
        docDescribe="결재 문서를 작성하고 결재선을 지정해 상신합니다."
        getBody={getBody}
        onSaved={() => navigate(`/project/${pid}/approval`)}
        hideSaveButton={true}
        mobileFullScreen={true}
      >
        <div className="min-h-screen bg-gray-50">
          {/* 모바일 본문 영역 */}
          <main className="px-4 pt-4 pb-24">
            {/* 페이지 타이틀 */}
            <header className="mb-4">
              <p className="text-xs text-gray-500">전자결재</p>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditMode ? "결재 문서 수정" : "결재 상신 작성"}
              </h2>
            </header>

            {/* 결재 현황 요약 카드 */}
            <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-blue-800">
                  결재 현황
                </span>
                {form.is_urgent && (
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                    긴급
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center py-2 rounded-lg bg-white/80">
                  <div className="text-xl font-bold text-blue-600">
                    {approvalLines.length}
                  </div>
                  <div className="text-[10px] text-gray-500">결재자</div>
                </div>
                <div className="text-center py-2 rounded-lg bg-white/80">
                  <div className="text-xl font-bold text-green-600">
                    {referenceUserIds.length}
                  </div>
                  <div className="text-[10px] text-gray-500">참조인</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {/* 결재 설정 섹션 */}
              <Section
                title="결재 설정"
                expanded={expandedSections.approvalSettings}
                onToggle={() => toggleSection("approvalSettings")}
                icon={<Users size={16} className="text-gray-500" />}
              >
                {/* 결재선 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-blue-500" />
                      <span className="text-sm font-medium text-gray-700">
                        결재선
                      </span>
                      {approvalLines.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
                          {approvalLines.length}명
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsApprovalLineModalOpen(true)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                    >
                      관리
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {/* 결재선 미리보기 */}
                  {approvalLines.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(orderGroups)
                        .sort((a, b) => parseInt(a) - parseInt(b))
                        .map((order) => {
                          const group = orderGroups[order];
                          return (
                            <div
                              key={order}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200"
                            >
                              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold">
                                {order}
                              </span>
                              {group.map((line, idx) => (
                                <span
                                  key={line.id}
                                  className="text-sm text-blue-700"
                                >
                                  {line.approver_name || "미선택"}
                                  {line.approval_category ===
                                    "pre_approval" && (
                                    <span className="text-xs text-blue-500 ml-0.5">
                                      (전결)
                                    </span>
                                  )}
                                  {idx < group.length - 1 && (
                                    <span className="mx-1 text-blue-300">
                                      ·
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-gray-50 border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 text-center">
                        결재선을 지정해주세요
                      </p>
                    </div>
                  )}

                  <div className="h-px bg-gray-100" />

                  {/* 참조인 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus size={16} className="text-green-500" />
                      <span className="text-sm font-medium text-gray-700">
                        참조인
                      </span>
                      {referenceUserIds.length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
                          {referenceUserIds.length}명
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsReferenceModalOpen(true)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 transition-colors"
                    >
                      관리
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {/* 참조인 미리보기 */}
                  {referenceUserIds.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {referenceUserIds.map((id) => {
                        const user = users.find((u) => u.id === id);
                        if (!user) return null;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-sm text-green-700"
                          >
                            {user.last_name}
                            {user.first_name}
                            <button
                              type="button"
                              onClick={() =>
                                setReferenceUserIds(
                                  referenceUserIds.filter((uid) => uid !== id)
                                )
                              }
                              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-green-200 text-green-500"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-gray-50 border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 text-center">
                        참조인 (선택사항)
                      </p>
                    </div>
                  )}
                </div>
              </Section>

              {/* 문서 정보 섹션 */}
              <Section
                title="문서 정보"
                expanded={expandedSections.docInfo}
                onToggle={() => toggleSection("docInfo")}
              >
                {/* 문서 유형 */}
                <InputField
                  label="문서 유형"
                  type="select"
                  value={form.document_type}
                  onChange={(v) =>
                    handleChange({
                      target: { name: "document_type", value: v },
                    })
                  }
                  disabled={isEditMode}
                  options={types.map((t) => ({
                    value: String(t.id),
                    label: t.name || t.title,
                  }))}
                  placeholder="유형 없음"
                />

                {/* 제목 */}
                <InputField
                  label="제목"
                  value={form.title}
                  onChange={(v) => setForm((prev) => ({ ...prev, title: v }))}
                  placeholder="제목을 입력하세요"
                />

                {/* 긴급 문서 토글 */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        form.is_urgent
                          ? "bg-red-100 text-red-600"
                          : "bg-gray-200 text-gray-400"
                      }`}
                    >
                      <AlertCircle size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        긴급 문서
                      </p>
                      <p className="text-xs text-gray-500">
                        목록에서 긴급 표시됨
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        is_urgent: !prev.is_urgent,
                      }))
                    }
                    className={`relative h-7 w-12 rounded-full transition-colors ${
                      form.is_urgent ? "bg-red-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                        form.is_urgent ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </Section>

              {/* 문서 내용 섹션 */}
              <Section
                title="문서 내용"
                expanded={expandedSections.content}
                onToggle={() => toggleSection("content")}
              >
                <div className="min-h-[350px]">
                  <TinyMCEeditor
                    initialValue={form.content}
                    editorRef={editorRef}
                  />
                </div>
              </Section>
            </div>
          </main>

          {/* 모바일 하단 고정 액션바 */}
          <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={triggerSave}
                disabled={approvalLines.length === 0}
                className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                상신하기
              </button>
            </div>
          </footer>
        </div>

        {/* 모달 */}
        <ApprovalLineModal
          isOpen={isApprovalLineModalOpen}
          onClose={() => setIsApprovalLineModalOpen(false)}
          users={users}
          approvalLines={approvalLines}
          onAddApprover={addApprover}
          onRemoveApprover={removeApprover}
          onMoveApprover={moveApproverByIndex}
          onMoveApproverForReorder={moveApproverByIndexForReorder}
          onApproverChange={handleApproverChange}
          onApprovalTypeChange={handleApprovalTypeChange}
          onApprovalCategoryChange={handleApprovalCategoryChange}
          onLoadTemplate={(templateLines) => setApprovalLines(templateLines)}
          onSaveTemplate={handleSaveTemplate}
          onDeleteline={onDeleteline}
          lines={lines}
        />
        <ReferenceModal
          isOpen={isReferenceModalOpen}
          onClose={() => setIsReferenceModalOpen(false)}
          approvalLines={approvalLines}
          selectedIds={referenceUserIds}
          onChange={setReferenceUserIds}
          onSave={(ids) => setReferenceUserIds(ids)}
        />
      </FormContentManager>
    </>
  );
}

// 섹션 카드 컴포넌트 (자재검수기록서와 동일한 스타일)
function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && <div className="p-4 space-y-3">{children}</div>}
    </section>
  );
}

// 입력 필드 컴포넌트 (자재검수기록서와 동일한 스타일)
function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
  options = [],
}) {
  if (type === "select") {
    return (
      <div>
        <label className="text-gray-500 text-xs mb-1 block">{label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
        >
          {options.length === 0 ? (
            <option value="">{placeholder || "선택 없음"}</option>
          ) : (
            options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))
          )}
        </select>
      </div>
    );
  }

  return (
    <div>
      <label className="text-gray-500 text-xs mb-1 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  );
}
